import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BrandMark } from "@bw/shared/components";

interface AuthCardProps {
  /** Optional task heading. When omitted, the brand wordmark is the page h1. */
  heading?: string;
  /** Truthful, non-numeric context below the wordmark. */
  tagline?: string;
  children: ReactNode;
}

const wordmark = (
  <>
    Badminton{" "}
    <Box component="span" sx={{ display: "block" }}>
      Warriors
    </Box>
  </>
);

/**
 * Standalone Player auth composition.
 *
 * The wordmark names login and registration. Task-specific recovery pages use
 * their task as the single h1 and keep the brand decorative. All presentation
 * is native MUI so these routes inherit the mounted Player color scheme,
 * typography, focus treatment, and responsive spacing.
 */
export function AuthCard({ heading, tagline, children }: AuthCardProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          md: "minmax(0, 1.08fr) minmax(0, 0.92fr)",
        },
        gridTemplateRows: { xs: "auto 1fr", md: "1fr" },
        alignItems: "stretch",
        width: "100%",
        minHeight: "100dvh",
      }}
    >
      <Stack
        sx={[
          {
            minWidth: 0,
            minHeight: { md: "100dvh" },
            justifyContent: "center",
            px: { xs: 3, sm: 6, md: "clamp(2.5rem, 6vw, 6.25rem)" },
            py: { xs: 3, md: 6 },
          },
          (theme) => ({
            backgroundColor: `var(--bw-palette-semantic-onSelected, ${theme.palette.background.paper})`,
            color: `var(--bw-palette-semantic-selected, ${theme.palette.text.primary})`,
            ...theme.applyStyles("light", {
              backgroundColor: `var(--bw-palette-semantic-selected, ${theme.palette.background.paper})`,
              color: `var(--bw-palette-semantic-onSelected, ${theme.palette.text.primary})`,
            }),
          }),
        ]}
      >
        <Stack
          spacing={{ xs: 0, md: 4 }}
          sx={{ width: "100%", maxWidth: 560, minWidth: 0, mx: "auto" }}
        >
          <Stack
            direction={{ xs: "row", md: "column" }}
            spacing={{ xs: 2, md: 4 }}
            useFlexGap
            sx={{
              flexWrap: "wrap",
              alignItems: { xs: "center", md: "flex-start" },
            }}
          >
            <Box
              sx={{
                width: { xs: 72, md: "clamp(17.5rem, 22vw, 21.25rem)" },
                maxWidth: "100%",
                flexShrink: 0,
                alignSelf: "center",
              }}
            >
              <BrandMark aria-hidden="true" size="100%" />
            </Box>
            <Typography
              component={heading ? "p" : "h1"}
              variant="h1"
              aria-label={heading ? undefined : "Badminton Warriors"}
              aria-hidden={heading ? true : undefined}
              sx={{
                flex: { xs: "1 1 9rem", md: "0 1 auto" },
                minWidth: 0,
                fontSize: { xs: "1.5rem", md: "clamp(3rem, 5.3vw, 4.75rem)" },
                lineHeight: 1.02,
                letterSpacing: "-0.045em",
                overflowWrap: "anywhere",
              }}
            >
              {wordmark}
            </Typography>
          </Stack>
          <Typography
            variant="body1"
            sx={{ display: { xs: "none", md: "block" }, maxWidth: "32ch" }}
          >
            {!heading && tagline
              ? tagline
              : "Book courts, find a game, and compete."}
          </Typography>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "center",
          minWidth: 0,
          px: { xs: 3, sm: 6, lg: 8 },
          py: { xs: 4, sm: 5, md: 6 },
        }}
      >
        <Stack spacing={3} sx={{ width: "100%", maxWidth: 420, minWidth: 0 }}>
          {heading ? (
            <Stack spacing={1.25}>
              <Typography
                component="h1"
                variant="h1"
                sx={{ overflowWrap: "anywhere" }}
              >
                {heading}
              </Typography>
              {tagline ? (
                <Typography color="text.secondary" variant="body2">
                  {tagline}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
          {children}
        </Stack>
      </Box>
    </Box>
  );
}
